<template>
  <div class="conversation-detail admin-dashboard-modern">
    <!-- Header with Back Button -->
    <div class="detail-header">
      <button class="back-btn" @click="goBack">
        <i class="fa fa-arrow-left"></i>
        Back to List
      </button>
    </div>

    <!-- Conversation Info Card -->
    <div class="panel-card info-card">
      <div class="panel-header">
        <h3 class="panel-title">
          <span class="type-icon" :class="conversation?.type === 'voice' ? 'voice' : 'text'">
            <span v-if="conversation?.type === 'voice'">🎤</span>
            <span v-else>💬</span>
          </span>
          {{ conversation?.type === 'voice' ? 'Voice Call' : 'Text Chat' }}
        </h3>
        <span class="status-badge" :class="conversation?.status || 'unknown'">
          {{ formatStatus(conversation?.status) }}
        </span>
      </div>
      <div class="panel-body">
        <div class="conversation-id">
          <span class="id-label">Conversation ID:</span>
          <span class="id-value">{{ truncateId(conversationId) }}</span>
        </div>

        <div class="meta-grid">
          <div class="meta-item">
            <span class="meta-label">Date</span>
            <span class="meta-value">{{ conversation?.formattedDate || 'Unknown' }}</span>
          </div>

          <div class="meta-item">
            <span class="meta-label">Duration</span>
            <span class="meta-value">{{ formatDuration(conversation?.metadata?.call_duration_secs) }}</span>
          </div>

          <div class="meta-item">
            <span class="meta-label">User</span>
            <span class="meta-value">{{ conversation?.user || 'Anonymous' }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Sentiment / Call Quality -->
    <div class="panel-card sentiment-card">
      <div class="panel-header">
        <h3 class="panel-title">
          <i class="fa fa-heartbeat panel-icon"></i>
          Sentiment &amp; Call Quality
        </h3>
        <span v-if="needsAttention" class="attention-badge">⚠️ Needs attention</span>
      </div>
      <div class="panel-body">
        <!-- Unscored state: no analysis available yet -->
        <div v-if="!hasAnalysis" class="not-scored">
          <span class="not-scored-icon">⏳</span>
          <span class="not-scored-text">Not yet scored</span>
        </div>

        <template v-else>
          <div class="sentiment-grid">
            <div class="sentiment-item">
              <span class="meta-label">Sentiment</span>
              <span
                v-if="patientSentiment"
                class="sentiment-chip"
                :class="sentimentChipClass(patientSentiment)"
              >{{ formatSentiment(patientSentiment) }}</span>
              <span v-else class="sentiment-chip sentiment-unknown">—</span>
            </div>

            <div class="sentiment-item">
              <span class="meta-label">Quality</span>
              <span class="meta-value">{{ csScoreDisplay }}</span>
            </div>

            <div class="sentiment-item">
              <span class="meta-label">Outcome</span>
              <span class="meta-value">{{ formatOutcome(outcome) }}</span>
            </div>

            <div class="sentiment-item">
              <span class="meta-label">Patient</span>
              <span class="meta-value">{{ patientId || '—' }}</span>
            </div>
          </div>

          <div v-if="reason" class="sentiment-reason">
            <span class="meta-label">Reason</span>
            <span class="reason-text">{{ reason }}</span>
          </div>
        </template>
      </div>
    </div>

    <!-- Audio Player for Voice Conversations -->
    <div v-if="isVoiceConversation" class="panel-card">
      <div class="panel-header">
        <h3 class="panel-title">
          <i class="fa fa-microphone panel-icon"></i>
          Call Recording
        </h3>
      </div>
      <div class="panel-body">
      
      <div v-if="isLoadingAudio" class="audio-loading">
        <div class="loading-spinner"></div>
        <div class="loading-text">Loading audio recording...</div>
      </div>
      
      <div v-else-if="audioError" class="audio-error">
        <div class="error-icon">⚠️</div>
        <div class="error-message">{{ audioError }}</div>
        <button class="retry-btn" @click="loadAudio">Try Again</button>
      </div>
      
      <div v-else-if="audioUrl && !isLoadingAudio && !audioError" class="audio-player">
        <audio 
          ref="player" 
          controls 
          class="audio-element" 
          :key="audioUrl"
          :src="audioUrl"
          type="audio/mpeg"
          @loadedmetadata="logAudioEvent('loadedmetadata')"
          @canplay="logAudioEvent('canplay')"
          @error="logAudioErrorEvent"
          @stalled="logAudioEvent('stalled')"
          @progress="logAudioEvent('progress')"
          @suspend="logAudioEvent('suspend')"
        >
          Your browser does not support the audio element.
        </audio>
        
        <div class="audio-controls">
          <button class="audio-btn download-btn" @click="downloadAudio">
            <i class="fa fa-download"></i>
            Download Recording
          </button>
        </div>
      </div>
      </div>
    </div>

    <!-- Conversation Transcript -->
    <div class="panel-card">
      <div class="panel-header">
        <h3 class="panel-title">
          <i class="fa fa-comments-o panel-icon"></i>
          Conversation Transcript
        </h3>
        <span v-if="hasTranscript" class="message-count">{{ transcript.length }} messages</span>
      </div>
      <div class="panel-body transcript-body">
      
      <div v-if="isLoading" class="loading-state">
        <div class="loading-spinner"></div>
        <div class="loading-text">Loading transcript...</div>
      </div>
      
      <div v-else-if="!hasTranscript" class="empty-transcript">
        <div class="empty-icon">📝</div>
        <div class="empty-text">No transcript available for this conversation</div>
      </div>
      
      <div v-else class="transcript">
        <div 
          v-for="(message, index) in transcript" 
          :key="index" 
          class="message"
          :class="{ 'user-message': message.role === 'user', 'bot-message': message.role === 'agent' }"
        >
          <div class="message-sender">{{ message.role === 'user' ? 'User' : 'Agent' }}</div>
          <div class="message-bubble">
            <div class="message-text">{{ message.message }}</div>
            <div class="message-time">{{ formatDuration(message.time_in_call_secs) }}</div>
          </div>
        </div>
      </div>
      </div>
    </div>

    <!-- Related Information (if any) -->
    <div v-if="hasRelatedInfo" class="panel-card">
      <div class="panel-header">
        <h3 class="panel-title">
          <i class="fa fa-info-circle panel-icon"></i>
          Related Information
        </h3>
      </div>
      <div class="panel-body">
      
      <div class="related-info-card">
        <div v-if="hasRelatedAppointment" class="related-appointment">
          <div class="appointment-icon">📅</div>
          <div class="appointment-details">
            <div class="appointment-title">Appointment Booked</div>
            <div class="appointment-info">{{ conversation?.relatedInfo?.appointment?.info }}</div>
            <div class="appointment-time">{{ conversation?.relatedInfo?.appointment?.time }}</div>
          </div>
        </div>
        
        <div v-if="conversation?.relatedInfo?.notes" class="related-notes">
          <div class="notes-title">Additional Notes:</div>
          <div class="notes-content">{{ conversation?.relatedInfo?.notes }}</div>
        </div>
      </div>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="detail-actions">
      <button class="action-btn delete-btn" @click="confirmDelete">
        <i class="fa fa-trash-o"></i>
        Delete Conversation
      </button>
    </div>
    
    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="delete-modal">
      <div class="modal-backdrop" @click="cancelDelete"></div>
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h3 class="modal-title">Confirm Delete</h3>
            <button class="modal-close" @click="cancelDelete">×</button>
          </div>
          <div class="modal-body">
            <p>Are you sure you want to delete this conversation?</p>
            <p class="text-danger">This action cannot be undone.</p>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="cancelDelete">Cancel</button>
            <button class="btn btn-danger" @click="deleteConversation">Delete</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters, mapMutations } from 'vuex';
import moment from 'moment-timezone';
import CommonOverlay from '@/components/CommonOverlay.vue';
import mixin from '@/mixin.js'; // Import the mixin

export default {
  name: 'ConversationDetail',
  mixins: [mixin], // Use the mixin
  components: {
    CommonOverlay
  },
  props: {
    conversationId: {
      type: String,
      required: true
    },
    // Optional explicit site id passed from the list. Falls back to the active
    // site (this.siteID from the mixin) when not provided. Required by the
    // backend ownership guard on the detail/audio endpoints.
    siteId: {
      type: String,
      default: null
    },
    // ── Analysis fields threaded through from the call-history list row
    // (getSiteCallHistory already returns these). Rendered in the Sentiment /
    // Call Quality panel WITHOUT any extra backend call. All optional so the
    // panel degrades to a "Not yet scored" state for unscored calls.
    patientSentiment: {
      type: String, // 'negative' | 'neutral' | 'positive' | null
      default: null
    },
    csScore: {
      type: Number, // 0-100 | null
      default: null
    },
    needsAttentionFlag: {
      type: [Number, Boolean], // 1 | 0 | null from the DB row
      default: null
    },
    reason: {
      type: String,
      default: null
    },
    outcome: {
      type: String,
      default: null
    },
    patientId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      showDeleteModal: false,
    };
  },
  computed: {
    // Map state from botAdmin module
    ...mapState('botAdmin', {
      conversation: state => state.elevenLabsConversationDetail,
      isLoading: state => state.isLoadingElevenLabsConversationDetail,
      audioUrl: state => state.elevenLabsAudioSrc,
      isLoadingAudio: state => state.elevenLabsLoadingAudio,
      audioError: state => state.elevenLabsAudioError
    }),
    isVoiceConversation() {
      // Check for metadata.call_duration_secs or has_audio field
      return !!(this.conversation?.metadata?.call_duration_secs > 0 || this.conversation?.has_audio);
    },
    transcript() {
      // Return transcript from conversation detail if available
      // Note: The detail API might not include the full transcript.
      // Adjust if transcript needs separate fetching.
      return this.conversation?.transcript || []; 
    },
    hasTranscript() {
      // Check the actual transcript from state
      return this.transcript && this.transcript.length > 0;
    },
    hasRelatedInfo() {
      return this.conversation?.relatedInfo && 
        (this.hasRelatedAppointment || this.conversation?.relatedInfo?.notes);
    },
    hasRelatedAppointment() {
      return this.conversation?.relatedInfo?.appointment?.info;
    },
    // True when at least one analysis field has a value. When every field is
    // null/empty the call hasn't been scored yet → show the subtle
    // "Not yet scored" state instead of an empty panel.
    hasAnalysis() {
      return !!(
        this.patientSentiment ||
        (this.csScore !== null && this.csScore !== undefined) ||
        this.needsAttention ||
        this.reason ||
        this.outcome ||
        (this.patientId !== null && this.patientId !== undefined && this.patientId !== '')
      );
    },
    needsAttention() {
      // DB returns 1|0; coerce to a truthy boolean for the badge.
      return !!this.needsAttentionFlag;
    },
    csScoreDisplay() {
      if (this.csScore === null || this.csScore === undefined) return '—';
      return `${this.csScore}/100`;
    }
  },
  created() {
    // console.log('ConversationDetail created for ID:', this.conversationId);
    // Don't fetch immediately. Wait for settings.
    // this.fetchData(); 
  },
  methods: {
    // Map actions from botAdmin module
    ...mapActions('botAdmin', [
      'fetchElevenLabsConversationDetail',
      'fetchElevenLabsAudio',
      'deleteElevenLabsConversation'
    ]),
    // Map necessary mutations (e.g., for clearing audio state)
    ...mapMutations('botAdmin', [
        'CLEAR_ELEVENLABS_AUDIO_DETAIL'
    ]),

    formatDuration(seconds) {
      if (seconds === null || seconds === undefined) return '-'; // Handle null/undefined
      const duration = moment.duration(seconds, 'seconds');
      const h = String(duration.hours()).padStart(1, '0');
      const m = String(duration.minutes()).padStart(2, '0');
      const s = String(duration.seconds()).padStart(2, '0');
      if (duration.hours() > 0) {
        return `${h}:${m}:${s}`;
      }
      return `${m}:${s}`;
    },

    // Maps a sentiment value to the chip CSS class. Kept consistent with the
    // chips ConversationList renders (sentiment-negative/neutral/positive,
    // sentiment-unknown for anything else). Pure → unit-tested.
    sentimentChipClass(sentiment) {
      switch (sentiment) {
        case 'negative':
          return 'sentiment-negative';
        case 'neutral':
          return 'sentiment-neutral';
        case 'positive':
          return 'sentiment-positive';
        default:
          return 'sentiment-unknown';
      }
    },
    formatSentiment(sentiment) {
      if (!sentiment) return '—';
      return sentiment.charAt(0).toUpperCase() + sentiment.slice(1);
    },
    formatOutcome(outcome) {
      if (!outcome) return '—';
      return String(outcome)
        .split('_')
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
    },

    async loadAudio() {
      // console.log('[ConversationDetail] loadAudio triggered.');
      this.CLEAR_ELEVENLABS_AUDIO_DETAIL(); // Clear previous audio state
      
      // Log current state of allSettings and siteID for debugging
      // console.log('[ConversationDetail] Current allSettings:', JSON.parse(JSON.stringify(this.allSettings)));
      // console.log('[ConversationDetail] Current siteID from this.siteID:', this.siteID);

      const currentSiteID = this.siteId || this.siteID;

      if (currentSiteID) { // Only check for siteID now
        // console.log(`[ConversationDetail] siteID (${currentSiteID}) found. Fetching audio for conversation: ${this.conversationId}`);
        try {
          await this.fetchElevenLabsAudio({ 
            conversationId: this.conversationId,
            siteID: currentSiteID // Pass siteID
            // apiKey is no longer passed
          });
          // console.log('[ConversationDetail] fetchElevenLabsAudio dispatched. Audio URL in state:', this.audioUrl);
          if (this.$refs.player && this.audioUrl) {
            this.$nextTick(() => {
              // console.log('[ConversationDetail] Audio player reference exists. Source set to:', this.$refs.player.src);
            });
          } else if (!this.audioUrl) {
            // console.warn('[ConversationDetail] Audio URL is not yet available in state after fetch.');
          }
        } catch (error) {
          // console.error('[ConversationDetail] Error dispatching fetchElevenLabsAudio:', error);
        }
      } else {
        const errorMessage = `[ConversationDetail] Cannot load audio: Missing siteID. Waiting for allSettings to be populated.`;
        // console.warn(errorMessage);
      }
    },
    // TEMPORARY TEST METHOD
    forcePlay() {
      if (this.$refs.player) {
        // Wait for the DOM update cycle to complete after src changes
        this.$nextTick(() => {
          // console.log('[forcePlay nextTick] Player ref exists. Current audioUrl state:', this.audioUrl);
          
          // Add a small extra delay
          setTimeout(() => {
            // console.log('[forcePlay setTimeout] Executing after additional delay.');
            // Ensure the ref still exists after nextTick and setTimeout
            if (this.$refs.player) { 
              // console.log('[forcePlay setTimeout] Audio element src:', this.$refs.player.currentSrc);
              // console.log('[forcePlay setTimeout] Audio element networkState:', this.$refs.player.networkState);
              // console.log('[forcePlay setTimeout] Audio element readyState:', this.$refs.player.readyState);
              
              // console.log('[forcePlay setTimeout] Attempting this.$refs.player.load()...');
              this.$refs.player.load(); // Explicitly tell the player to load the new source

              // console.log('[forcePlay setTimeout] Attempting programmatic play...');
              this.$refs.player.play().then(() => {
                // console.log('[forcePlay setTimeout] Programmatic play successful.');
              }).catch(error => {
                // console.error('[forcePlay setTimeout] Programmatic play failed:', error);
              });
            } else {
              // console.error('[forcePlay setTimeout] Cannot force play: Player ref disappeared after delay.');
            } 
          }, 100); // 100ms delay         

        });
      } else {
        // console.error('[forcePlay] Cannot force play: Player ref not found initially.');
      }
    },
    // NEW: Methods to log audio events
    logAudioEvent(eventName) {
      // console.log(`[Audio Event] ${eventName} triggered. Player:`, this.$refs.player);
      if (this.$refs.player) {
        // console.log(`[Audio Event ${eventName}] src: ${this.$refs.player.currentSrc}, duration: ${this.$refs.player.duration}, networkState: ${this.$refs.player.networkState}, readyState: ${this.$refs.player.readyState}`);
      }
    },
    logAudioErrorEvent(event) {
      const player = this.$refs.player;
      let errorDetails = 'Unknown error';
      if (player && player.error) {
        switch (player.error.code) {
          case player.error.MEDIA_ERR_ABORTED:
            errorDetails = 'MEDIA_ERR_ABORTED: The fetching process for the media resource was aborted by the user agent at the user\'s request.';
            break;
          case player.error.MEDIA_ERR_NETWORK:
            errorDetails = 'MEDIA_ERR_NETWORK: A network error of some description caused the user agent to stop fetching the media resource, after a DNS error or server connection failure.';
            break;
          case player.error.MEDIA_ERR_DECODE:
            errorDetails = 'MEDIA_ERR_DECODE: An error of some description occurred while decoding the media resource, after the resource was established to be usable.';
            break;
          case player.error.MEDIA_ERR_SRC_NOT_SUPPORTED:
            errorDetails = 'MEDIA_ERR_SRC_NOT_SUPPORTED: The media resource indicated by the src attribute or assigned to the media provider object was not suitable.';
            break;
          default:
            errorDetails = `An unknown error occurred. Code: ${player.error.code}`;
        }
        // console.error(`[Audio Event] Error: ${errorDetails}`, player.error);
      } else {
        // console.error('[Audio Event] Error event triggered, but no error object on player.', event);
      }
    },
    downloadAudio() {
      // Downloading base64 data URI might require different approach
      // For now, log and maybe show a message
      // console.log(`Download requested for conversation ${this.conversationId}`);
      if (this.audioUrl) {
        // Simple download attempt, might not work well for large base64
        const link = document.createElement('a');
        link.href = this.audioUrl;
        link.download = `conversation-${this.conversationId}.mp3`; // Adjust extension based on actual content type if known
        link.click();
        this.$toast?.info('Attempting to download audio...');
      } else {
        this.$toast?.error('Audio not loaded, cannot download.');
      }
    },
    goBack() {
      this.$emit('closePanel');
    },
    confirmDelete() {
      this.showDeleteModal = true;
    },
    cancelDelete() {
      this.showDeleteModal = false;
    },
    async deleteConversation() { // Make async for await
      if (!this.conversationId) return;
      // console.log(`Deleting conversation ${this.conversationId}`);
      this.showDeleteModal = false;
      try {
        const result = await this.deleteElevenLabsConversation(this.conversationId);
        if (result.success) {
          this.$toast?.success('Conversation deleted successfully.');
          this.$emit('closePanel');
        } else {
          this.$toast?.error(result.error || 'Failed to delete conversation.');
        }
      } catch (error) {
        // console.error('Error during deleteConversation call:', error);
        this.$toast?.error('An error occurred while deleting.');
      }
    },
    truncateId(id) {
      if (!id) return '';
      // Display full ID on detail page
      return id;
    },
    formatStatus(status) {
      if (!status) return 'Unknown';
      
      // Format status for display
      switch (status) {
        case 'in-progress':
          return 'In Progress';
        case 'completed':
          return 'Completed';
        case 'failed':
          return 'Failed';
        default:
          return status;
      }
    },
    fetchData() {
      if (!this.conversationId) {
        // console.error('No conversation ID provided to ConversationDetail.');
        // Optionally set an error state or display a message
        return;
      }
      // console.log(`Fetching details for conversation: ${this.conversationId}`);
      const effectiveSiteID = this.siteId || this.siteID;
      this.fetchElevenLabsConversationDetail({ conversationId: this.conversationId, siteID: effectiveSiteID })
        .catch(error => {
          // console.error('Error fetching conversation detail in component:', error);
          // Handle error display in UI if needed
        });
    }
  },
  watch: {
    conversationId(newId, oldId) {
      if (newId && newId !== oldId) {
        // console.log('ConversationDetail conversationId prop changed:', newId);
        // Clear previous audio state when ID changes
        this.CLEAR_ELEVENLABS_AUDIO_DETAIL();
        // Wait for settings before fetching new data
        if (this.allSettings) {
          this.fetchData();
        } else {
          // console.log('conversationId changed, but settings not ready. Waiting for allSettings watcher.')
          // The allSettings watcher will handle fetching data when ready
        }
      }
    },
    // Watcher for settings from the mixin
    allSettings: {
      immediate: true, // Check immediately on component creation
      handler(newSettings, oldSettings) { // Add oldSettings to only log once
        if (newSettings && !oldSettings) { // Log only when settings first become available
          // console.log('ConversationDetail: allSettings first available:', JSON.parse(JSON.stringify(newSettings))); // Deep clone for logging
        }
        if (newSettings && this.conversationId) {
          // console.log('ConversationDetail: allSettings available, fetching initial data.');
          this.fetchData();
        } else if (!newSettings) {
          // console.log('ConversationDetail: Waiting for allSettings to become available...');
        }
      }
    },
    // Watcher for the conversation computed property loading
    conversation(newConversation, oldConversation) {
      // Check if it's a genuinely new conversation object and not just an internal update
      if (newConversation && newConversation !== oldConversation) { 
          // console.log('Conversation data updated:', newConversation);
          if (this.isVoiceConversation) {
              // console.log('Conversation is voice type. Checking if settings are ready to load audio.');
              // Explicitly set loading for audio here *before* calling loadAudio.
              this.$store.commit('botAdmin/SET_ELEVENLABS_LOADING_AUDIO', true);
              
              // Only call loadAudio if settings are also ready
              if (this.allSettings) {
                 this.loadAudio();
              } else {
                 // console.log('Conversation updated, but settings not ready. Audio load deferred.');
                 // The allSettings watcher will eventually call fetchData, which updates conversation,
                 // which will re-trigger this logic, and then loadAudio if settings are present.
              }
          } else {
              // console.log('Conversation updated, but it is not a voice conversation.');
              // Ensure audio loading is false if it's not a voice conversation
              this.$store.commit('botAdmin/SET_ELEVENLABS_LOADING_AUDIO', false);
          }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../AppointmentCalendarModern.scss';

.conversation-detail {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1.25rem;
  height: 100%;
  overflow-y: auto;
}

/* Header */
.detail-header {
  display: flex;
  align-items: center;
}

.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background-color: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  color: #334155;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.875rem;
  font-weight: 500;
}

.back-btn:hover {
  background-color: #f8fafc;
  border-color: #cbd5e1;
}

.back-btn i {
  font-size: 0.75rem;
}

/* Panel Card Pattern */
.panel-card {
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
}

.panel-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  color: #0f172a;
  margin: 0;
}

.panel-icon {
  color: #3abf94;
  font-size: 1rem;
}

.panel-body {
  padding: 1.25rem;
}

/* Info Card */
.info-card .panel-body {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.conversation-id {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: #f8fafc;
  border-radius: 8px;
  font-size: 0.875rem;
}

.id-label {
  color: #64748b;
  font-weight: 500;
}

.id-value {
  color: #0f172a;
  font-family: 'Monaco', 'Consolas', monospace;
  font-size: 0.8125rem;
  word-break: break-all;
}

.meta-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 1rem;
}

.meta-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.meta-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

.meta-value {
  font-size: 0.9375rem;
  color: #0f172a;
  font-weight: 500;
}

/* Type Icon */
.type-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  font-size: 0.875rem;
}

.type-icon.text {
  background: rgba(99, 102, 241, 0.1);
}

.type-icon.voice {
  background: rgba(168, 85, 247, 0.1);
}

/* Status Badges */
.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  border-radius: 50px;
  font-size: 0.75rem;
  font-weight: 500;
}

.status-badge.completed {
  background: rgba(58, 191, 148, 0.1);
  color: #3abf94;
}

.status-badge.in-progress {
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
}

.status-badge.failed {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.status-badge.unknown {
  background: #f1f5f9;
  color: #64748b;
}

.message-count {
  font-size: 0.8125rem;
  font-weight: 500;
  color: #64748b;
  background: #e2e8f0;
  padding: 0.25rem 0.75rem;
  border-radius: 50px;
}

/* Sentiment / Call Quality Panel */
.sentiment-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 1rem;
}

.sentiment-item {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  align-items: flex-start;
}

/* Sentiment chip — mirrors ConversationList: red=negative / grey=neutral /
   green=positive / faint dash for unknown. */
.sentiment-chip {
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 50px;
  font-size: 0.75rem;
  font-weight: 600;
}

.sentiment-chip.sentiment-negative {
  background-color: #fee2e2;
  color: #dc2626;
}

.sentiment-chip.sentiment-neutral {
  background-color: #f1f5f9;
  color: #64748b;
}

.sentiment-chip.sentiment-positive {
  background-color: #dcfce7;
  color: #16a34a;
}

.sentiment-chip.sentiment-unknown {
  background-color: transparent;
  color: #94a3b8;
  font-weight: 500;
}

/* Prominent "Needs attention" badge in the panel header */
.attention-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  border-radius: 50px;
  font-size: 0.75rem;
  font-weight: 700;
  background-color: #fee2e2;
  color: #dc2626;
  white-space: nowrap;
}

.sentiment-reason {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #f1f5f9;
}

.reason-text {
  font-size: 0.875rem;
  color: #334155;
  line-height: 1.5;
}

/* Unscored state */
.not-scored {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #94a3b8;
  font-size: 0.875rem;
  font-style: italic;
}

.not-scored-icon {
  font-size: 1rem;
  font-style: normal;
}

/* Audio Player Styles */
.audio-loading,
.audio-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  gap: 1rem;
  text-align: center;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e2e8f0;
  border-top-color: #3abf94;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-text {
  color: #64748b;
  font-size: 0.875rem;
}

.error-icon {
  font-size: 2rem;
}

.error-message {
  color: #ef4444;
  font-size: 0.875rem;
}

.retry-btn {
  padding: 0.5rem 1rem;
  background-color: #3abf94;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: background-color 0.2s ease;
}

.retry-btn:hover {
  background-color: #2da57f;
}

.audio-player {
  width: 100%;
}

.audio-element {
  width: 100%;
  margin-bottom: 1rem;
  border-radius: 8px;
}

.audio-controls {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

.audio-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s ease;
}

.download-btn {
  background-color: #fff;
  border: 1px solid #e2e8f0;
  color: #334155;
}

.download-btn:hover {
  background-color: rgba(58, 191, 148, 0.1);
  border-color: #3abf94;
  color: #3abf94;
}

/* Transcript Styles */
.transcript-body {
  padding: 0;
}

.loading-state,
.empty-transcript {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  text-align: center;
}

.empty-icon {
  font-size: 2.5rem;
  color: #cbd5e1;
  margin-bottom: 1rem;
}

.empty-text {
  font-size: 1rem;
  font-weight: 600;
  color: #334155;
}

.transcript {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-height: 400px;
  overflow-y: auto;
  padding: 1.25rem;
}

.message {
  display: flex;
  flex-direction: column;
  max-width: 85%;
}

.user-message {
  align-self: flex-end;
}

.bot-message {
  align-self: flex-start;
}

.message-sender {
  font-size: 0.75rem;
  margin-bottom: 0.25rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

.user-message .message-sender {
  text-align: right;
  color: #3abf94;
}

.bot-message .message-sender {
  text-align: left;
  color: #6366f1;
}

.message-bubble {
  padding: 0.75rem 1rem;
  border-radius: 12px;
  position: relative;
}

.user-message .message-bubble {
  background-color: rgba(58, 191, 148, 0.1);
  border-top-right-radius: 4px;
}

.bot-message .message-bubble {
  background-color: #f1f5f9;
  border-top-left-radius: 4px;
}

.message-text {
  font-size: 0.9375rem;
  line-height: 1.5;
  color: #334155;
  margin-bottom: 0.5rem;
}

.message-time {
  text-align: right;
  font-size: 0.6875rem;
  color: #94a3b8;
}

/* Related Info Styles */
.related-info-card {
  background-color: #f8fafc;
  border-radius: 8px;
  padding: 1rem;
}

.related-appointment {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}

.appointment-icon {
  font-size: 1.5rem;
  color: #3abf94;
}

.appointment-details {
  flex: 1;
}

.appointment-title {
  font-weight: 600;
  margin-bottom: 0.25rem;
  color: #3abf94;
}

.appointment-info {
  margin-bottom: 0.25rem;
  font-size: 0.9375rem;
  color: #334155;
}

.appointment-time {
  font-size: 0.8125rem;
  color: #64748b;
}

.related-notes {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;
}

.notes-title {
  font-weight: 600;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  color: #0f172a;
}

.notes-content {
  font-size: 0.875rem;
  color: #64748b;
}

/* Action Buttons */
.detail-actions {
  display: flex;
  justify-content: flex-end;
  padding-top: 1rem;
  margin-top: auto;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s ease;
}

.delete-btn {
  background-color: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.2);
}

.delete-btn:hover {
  background-color: rgba(239, 68, 68, 0.15);
  border-color: rgba(239, 68, 68, 0.3);
}

/* Modal Styles */
.delete-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(15, 23, 42, 0.5);
  z-index: 1040;
}

.modal-dialog {
  position: relative;
  z-index: 1050;
  width: 90%;
  max-width: 400px;
}

.modal-content {
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  border: 1px solid #e2e8f0;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e2e8f0;
  background-color: #f8fafc;
}

.modal-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #0f172a;
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
  color: #64748b;
  padding: 0;
  transition: color 0.2s ease;
}

.modal-close:hover {
  color: #334155;
}

.modal-body {
  padding: 1.5rem 1.25rem;
}

.modal-body p {
  margin: 0 0 0.5rem;
  color: #334155;
  font-size: 0.9375rem;
}

.text-danger {
  color: #ef4444;
  font-size: 0.875rem;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  border-top: 1px solid #e2e8f0;
  background-color: #f8fafc;
}

.btn {
  padding: 0.5rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s ease;
}

.btn-secondary {
  background-color: #fff;
  border: 1px solid #e2e8f0;
  color: #334155;
}

.btn-secondary:hover {
  background-color: #f1f5f9;
}

.btn-danger {
  background-color: #ef4444;
  border: 1px solid #ef4444;
  color: white;
}

.btn-danger:hover {
  background-color: #dc2626;
}

/* Responsive Adjustments */
@media (max-width: 768px) {
  .meta-grid {
    grid-template-columns: 1fr 1fr;
  }

  .message {
    max-width: 95%;
  }
}

@media (max-width: 480px) {
  .conversation-detail {
    padding: 1rem;
  }

  .meta-grid {
    grid-template-columns: 1fr;
  }
}
</style> 